/**
 * @file Renders the base modal with the platform's shared style defaults.
 */
import React from "react";
import { Modal as AntModal } from "antd";
import type { ModalProps as AntModalProps } from "antd";

import { W1, W2, MODAL_WIDTH } from "@components/Constants";

type Props = {
  width?: AntModalProps["width"];
  title?: AntModalProps["title"];
  footer?: AntModalProps["footer"];
  className?: string;
  bodyStyle?: React.CSSProperties;
  isOpen: boolean;
  onClose: AntModalProps["onCancel"];
  children?: React.ReactNode;
} & Omit<
  AntModalProps,
  | "open"
  | "onCancel"
  | "title"
  | "width"
  | "footer"
  | "className"
  | "children"
  | "styles"
>;

/**
 * Renders an Ant Design modal with the platform's default width, position
 * and body spacing, and an optional default footer when an OK action is set.
 */
const Modal = ({
  width = MODAL_WIDTH,
  title = undefined,
  footer = null,
  className = "",
  bodyStyle = undefined,
  isOpen,
  onClose,
  children,
  ...otherModalProps
}: Props): JSX.Element => {
  const styles = {
    body: {
      marginTop: W1,
      ...(bodyStyle || {}),
    },
  };

  const style = {
    top: W2,
    transform: "translateZ(0)",
  };

  const hasOkAction = otherModalProps.okText !== undefined
    || otherModalProps.onOk !== undefined;
  const shouldRenderDefaultFooter = footer === null && hasOkAction;

  const modalProps = {
    open: isOpen,
    onCancel: onClose,
    maskClosable: false,
    style,
    title,
    width,
    footer: shouldRenderDefaultFooter ? undefined : footer,
    styles,
    className,
    ...otherModalProps,
  };

  return (
    <AntModal {...modalProps}>
      {children}
    </AntModal>
  );
};

export default Modal;
